import { FormControl, MenuItem, Select } from '@material-ui/core';
import WhiteButton from 'components/common/button/WhiteButton';
import EscapeConvert from 'components/common/convert/EscapeConvert';
import MuiWrapper from 'components/common/input/MuiWrapper';
import CustomText from 'components/common/text/CustomText';
import T from 'components/common/text/T';
import ProjectInfoHistoryContainer from 'containers/project/ProjectInfoHistoryContainer';
import { genderList } from 'lib/mapper';
import { IProjectInfo, IRemake, IRework } from 'lib/types/projectTypes';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface ProjectInfoProps {
  extraDesignOp: any;
  reworkListCt: {
    value: IRework[];
    onChange: (e: any) => void;
    setValue: (value: IRework[]) => void;
  };
  indicationFormat: object | any;
  indicationInfo: object | any;
  language: string;
  hasAlarm: any;
  projectInfo: IProjectInfo;
  isProjectCreator: boolean;
  managerList: object | any;
  remakeList: IRemake[];
  prevPartnerManager: number | null;
  partnerManager: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  onChangePartnerManager: (number: number) => void;
  onOpenReworkStatusModal: () => void;
}

function ProjectInfo({
  extraDesignOp,
  reworkListCt,
  language,
  hasAlarm,
  projectInfo,
  isProjectCreator,
  managerList,
  remakeList,
  prevPartnerManager,
  partnerManager,
  onChangePartnerManager,
  onOpenReworkStatusModal,
}: ProjectInfoProps) {
  const { isClinicUser } = useShallowAppSelector(state => ({
    isClinicUser: isClinicUserSelector(state),
  }));
  const {
    clientName,
    projectId,
    dueDate,
    manager,
    senderMemo,
    ownerName,
    partnerIdx,
    patientInfo,
    ownerRemakeType,
  } = projectInfo;

  const { selectedRestorationPontic, selectedceramicMatel, selectedOcclusalSurface } =
    extraDesignOp;

  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const handleClickHistory = () => {
    setHistoryOpen(!historyOpen);
  };

  return (
    <Styled.ProjectInfo data-component-name="ProjectInfo">
      <div className="projectInfo__content_title_box">
        <h2 className="sr-only">
          <T>PROJECT_INFORMATION</T>
        </h2>
      </div>
      <div className="projectInfo__content_wrapper">
        <div className="projectInfo__content_box box1">
          <div className="projectInfo__content_row projectId">
            <div className="projectInfo__content_label">
              <T>PROJECT_ID</T>
            </div>
            <div className="projectInfo__content_detail projectId">
              <div className="projectInfo__content projectId">{projectId}</div>
            </div>
          </div>
          <div className="projectInfo__content_row client">
            <div className="projectInfo__content_label">
              {isClinicUser ? <T>GLOBAL_PATIENT</T> : <T>GLOBAL_CLIENT</T>}
            </div>
            <div className="projectInfo__content_detail client">
              <div className="projectInfo__content client">
                {isClinicUser ? (
                  <>{`${patientInfo?.firstName} ${
                    !!patientInfo?.lastName ? patientInfo.lastName : ''
                  }${!!patientInfo?.identification ? patientInfo?.identification : ''}, ${
                    genderList[patientInfo?.gender as keyof typeof genderList]
                  }${
                    moment(patientInfo?.birthday).isValid() ? `, ${patientInfo.birthday}` : ''
                  }`}</>
                ) : (
                  <>{isProjectCreator ? clientName : ownerName}</>
                )}
              </div>
            </div>
          </div>

          <div className="projectInfo__content_row duedate">
            <div className="projectInfo__content_label">
              <T>PROJECT_DUE_DATE</T>
            </div>
            <div className="projectInfo__content_detail duedate">
              <div className="projectInfo__content duedate">
                {!dueDate
                  ? '-'
                  : language === 'en'
                  ? moment.unix(dueDate).locale(language).format('MMM. DD. YYYY HH:mm')
                  : moment.unix(dueDate).locale(language).format('YYYY. MM. DD  HH:mm')}
              </div>
            </div>
          </div>

          <div className="projectInfo__content_row manager">
            <div className="projectInfo__content_label">
              <T>PROJECT_MANAGER</T>
            </div>
            {isProjectCreator || !partnerIdx ? (
              <div className="projectInfo__content_detail manager">
                <div className="projectInfo__content manager">
                  {isProjectCreator ? manager : '-'}
                </div>
              </div>
            ) : (
              <>
                <div className="projectInfo__content_detail selectManager">
                  <div className="projectInfo__content selectManager">
                    {!hasAlarm && (
                      <MuiWrapper className="xs partner_select_wrapper">
                        <FormControl variant="outlined" className="partner_select_box">
                          <Select
                            className="partner_select"
                            MenuProps={{
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                            disabled={!partnerIdx}
                            name="partnerManager"
                            displayEmpty
                            value={partnerManager.value}
                            onChange={partnerManager.onChange}
                          >
                            {managerList.length > 0 &&
                              managerList.map((item: any, idx: number) => {
                                return (
                                  <MenuItem value={`${item.employeeIdx}`} key={idx}>
                                    {item.userName}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>
                      </MuiWrapper>
                    )}
                  </div>
                </div>
                <div className="projectInfo__content_button">
                  <WhiteButton
                    width={100}
                    height={34}
                    disabled={!partnerIdx || Number(partnerManager.value) === prevPartnerManager}
                    borderRadius={50}
                    onClick={() => onChangePartnerManager(Number(partnerManager.value))}
                  >
                    <CustomText fontSize={14} fontWeight={400}>
                      <T>GLOBAL_SAVE</T>
                    </CustomText>
                  </WhiteButton>
                </div>
              </>
            )}
          </div>
          <div className="projectInfo__content_row rework">
            <div className="projectInfo__content_label">
              <T>REWORK_PROGRESS</T>
            </div>
            <div className="projectInfo__content_detail rework">
              <div className="projectInfo__content rework">
                {useMemo(
                  () =>
                    reworkListCt.value?.length > 0 ? (
                      reworkListCt.value.map((item: IRework, index: number) => {
                        const text =
                          index === 0
                            ? `${t(`REWORK_TYPE_${item.reworkTypeIdx}`)}`
                            : `, ${t(`REWORK_TYPE_${item.reworkTypeIdx}`)}`;

                        return <span key={index}>{text}</span>;
                      })
                    ) : (
                      <span>
                        <T>GLOBAL_NONE</T>
                      </span>
                    ),
                  [reworkListCt.value],
                )}
              </div>
            </div>
            <div className="projectInfo__content_button">
              <WhiteButton
                width={100}
                height={34}
                borderRadius={50}
                onClick={() => onOpenReworkStatusModal()}
              >
                <CustomText fontSize={14} fontWeight={400}>
                  <T>GLOBAL_EDIT</T>
                </CustomText>
              </WhiteButton>
            </div>
          </div>
          <div className="projectInfo__content_row remake">
            <div className="projectInfo__content_label">
              <T>GLOBAL_REMAKE</T>
            </div>
            <div className="projectInfo__content_detail remake">
              <div className="projectInfo__content remake">
                {ownerRemakeType?.length > 0 ? (
                  ownerRemakeType.map((item, index) => {
                    return (
                      <p key={index}>
                        {item.remakeTypeIdx}.
                        {
                          remakeList?.find(
                            (remake: IRemake) => remake.remakeTypeIdx === item.remakeTypeIdx,
                          )?.remakeTypeName
                        }
                      </p>
                    );
                  })
                ) : (
                  <p>
                    <T>GLOBAL_NONE</T>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="projectInfo__content_box box2">
          {(!!selectedRestorationPontic || !!selectedceramicMatel || !!selectedOcclusalSurface) && (
            <div className="projectInfo__content_row design">
              <div className="projectInfo__content_detail design">
                {selectedRestorationPontic && (
                  <div className="projectInfo__content pontic">
                    <figure className="pontic">
                      <img src={selectedRestorationPontic?.src} alt="tooth design" />
                    </figure>
                    <div className="projectInfo__content_text">
                      <T>SUMMARY_PONTIC_DESIGN</T>
                    </div>
                  </div>
                )}
                {selectedceramicMatel && (
                  <div className="projectInfo__content ceramic">
                    <figure className="ceramic">
                      <img src={selectedceramicMatel?.src} alt="tooth design" />
                    </figure>
                    <div className="projectInfo__content_text">
                      <T>SUMMARY_MATEL_DESIGN</T>
                    </div>
                  </div>
                )}

                {selectedOcclusalSurface && (
                  <div className="projectInfo__content occlusal">
                    <figure className="occlusal">
                      <img src={selectedOcclusalSurface?.src} alt="tooth design" />
                    </figure>
                    <div className="projectInfo__content_text">
                      <T>SUMMARY_SURFACE_DESIGN</T>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="projectInfo__content_row memo">
            <div className="projectInfo__content_detail memo">
              <div className="projectInfo__content memo">
                <div className="memo_box">
                  <EscapeConvert content={senderMemo} />
                </div>
              </div>
            </div>
          </div>
          {useMemo(() => {
            return (
              <ProjectInfoHistoryContainer isOpen={historyOpen} onClick={handleClickHistory} />
            );
          }, [historyOpen, handleClickHistory])}
        </div>
      </div>
    </Styled.ProjectInfo>
  );
}

export default React.memo(ProjectInfo);

const Styled = {
  ProjectInfo: styled.div<{}>`
    width: 100%;
    height: inherit;
    .projectInfo__content_wrapper {
      height: inherit;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .projectInfo__content_box {
        &.box1 {
          display: flex;
          flex-direction: column;
          row-gap: 10px;
          .projectInfo__content_row {
            width: 100%;
            display: flex;

            .projectInfo__content_label {
              width: 150px;
              display: flex;
              align-items: flex-start;
              justify-content: flex-start;
              font-size: 14px;
              font-weight: 600;
              min-height: 38px;
              padding: 10px 0;
            }
            .projectInfo__content_detail {
              width: calc(100% - 150px);
              word-break: break-all;
              white-space: break-spaces;
              min-height: 38px;
              display: flex;
              align-items: center;

              &.remake {
                overflow-y: scroll;
              }
              &.selectManager {
                padding: 0;
                .partner_select {
                  width: 200px;
                }
              }
              &.selectManager,
              &.rework {
                width: calc(100% - 250px);
                column-gap: 10px;
              }

              .projectInfo__content {
                font-size: 13px;
                font-weight: 400;
                line-height: 17px;
                display: flex;
                row-gap: 10px;
                max-height: 100px;
                &:not(.rework) {
                  flex-direction: column;
                }
              }
            }

            .projectInfo__content_button {
              height: 38px;
              display: flex;
              align-items: center;
            }
          }
        }

        &.box2 {
          .projectInfo__content_row {
            width: 100%;
            display: flex;
            &.memo {
              padding: 0;
              flex-direction: column;
              margin-bottom: 27px;
              .projectInfo__content_detail {
                width: 100%;
                height: 90px;
                border: 1px solid ${color.border_line};
                border-radius: 3px;
                overflow-y: scroll;
                padding: 0;
                display: block;
                .memo_box {
                  padding: 5px;
                  line-height: 20px;
                }
              }
            }
            &.design {
              margin-bottom: 10px;
              .projectInfo__content_detail {
                display: flex;
                width: 100%;
                height: 70px;
                border: 1px solid ${color.border_line};
                border-radius: 3px;
                padding: 15px;
                justify-content: flex-start;
                column-gap: 20px;

                .projectInfo__content {
                  display: flex;
                  column-gap: 10px;
                  align-items: center;
                  figure {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    width: 40px;
                    height: 40px;
                    box-shadow: 0 0 3px rgba(0, 0, 0, 0.16);
                    border-radius: 5px;
                    &.pontic {
                      align-items: flex-end;
                      img {
                        height: 82%;
                      }
                    }
                    &.ceramic img {
                      height: 65%;
                    }
                    &.occlusal img {
                      width: 75%;
                    }
                  }

                  .projectInfo__content_text {
                    font-size: 13px;
                    line-height: 18px;
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
};
